import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import styled, { ThemeProvider } from 'styled-components';
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom';
import Moment from 'react-moment';
import { darkTheme } from '../../style/Global';
import { API_URL_V2, VIDEO_SERVICE_URL } from '../../config/api';
import { classifyCutResult } from '../tournametns/player/helpers/videoCutResult';
import logo from '../../images/ic_logo.svg';
import { formatSeconds } from '../player/matchEvents';

const CLIP_BEFORE = 10;
const CLIP_AFTER = 10;

function clipOffset(value, fallback) {
  if (value === null || !/^\d+$/.test(value)) return fallback;
  const seconds = Number(value);
  return Number.isSafeInteger(seconds) && seconds <= 300 ? seconds : fallback;
}

function groupClips(clips, mode) {
  if (mode === 'none') return [{ key: 'all', label: 'All events', clips }];
  const groups = new Map();
  for (const clip of clips) {
    const match = clip.match;
    const key = mode === 'type'
      ? `type:${clip.paramName.trim().toLowerCase()}`
      : `match:${match?.id ?? JSON.stringify([clip.date, match?.home_team, match?.away_team, match?.competition])}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: mode === 'type' ? clip.paramName : match
          ? `${match.home_team || '?'} vs ${match.away_team || '?'}`
          : 'Match unavailable',
        date: mode === 'match' ? clip.date : null,
        clips: [],
      });
    }
    groups.get(key).clips.push(clip);
  }
  return [...groups.values()];
}

// /shared/playlist/:token — public, no-auth viewer for a Player PWA share
// link. Hits /api/v2/shared/video-list/{token} which returns hydrated marker
// data without authentication. Request a real cut for each event so native
// controls expose only that clip, then advance when the cut ends.
const SharedPlaylist = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const { search } = useLocation();
  const requestedBefore = clipOffset(searchParams.get('seconds_before'), CLIP_BEFORE);
  const requestedAfter = clipOffset(searchParams.get('seconds_after'), CLIP_AFTER);
  const emptyWindow = requestedBefore + requestedAfter === 0;
  const secondsBefore = emptyWindow ? CLIP_BEFORE : requestedBefore;
  const secondsAfter = emptyWindow ? CLIP_AFTER : requestedAfter;
  const videoRef = useRef(null);
  const clipCache = useRef(new Map());
  const playlistDownloadController = useRef(null);
  const playlistDownloadTrigger = useRef(null);
  const playlistDownloadMenu = useRef(null);
  const [preparedClip, setPreparedClip] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadingPlaylist, setDownloadingPlaylist] = useState(false);
  const [playlistFormat, setPlaylistFormat] = useState('zip');
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);
  const [playlistDownloadError, setPlaylistDownloadError] = useState(null);

  const [data, setData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [grouping, setGrouping] = useState('none');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The API returns 410 Gone once the share TTL has elapsed; we surface a
  // friendlier message for that case instead of the generic load error.
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setLoading(true);
    setDownloadingPlaylist(false);
    setPlaylistFormat('zip');
    setFormatMenuOpen(false);
    setPlaylistDownloadError(null);
    setData(null);
    setActiveIndex(0);
    setGrouping('none');
    setSelectedGroup('all');
    setError(null);
    setExpired(false);
    setPreparedClip(null);
    clipCache.current.clear();
    // Bare axios.get — no Authorization header. The public endpoint must
    // accept anonymous calls; sending a token would still work but is
    // unnecessary.
    axios.get(`${API_URL_V2}/shared/video-list/${token}`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 410) {
          setExpired(true);
        } else {
          setError(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  const events = useMemo(() => {
    const raw = data?.events ?? [];
    return raw.map((e) => ({
      key: String(e.id),
      paramName: e.action || 'Event',
      second: Number(e.second) || 0,
      videoSecond: typeof e.video_second === 'number' && Number.isFinite(e.video_second)
        ? e.video_second
        : Number(e.second) || 0,
      videoLink: e.video,
      match: e.match,
      date: Number.isFinite(Date.parse(e.match?.date)) ? e.match.date : null,
    })).sort((a, b) => {
      const aTime = Date.parse(a.match?.date);
      const bTime = Date.parse(b.match?.date);
      const aDate = Number.isFinite(aTime) ? aTime : -Infinity;
      const bDate = Number.isFinite(bTime) ? bTime : -Infinity;
      // Newest match first; within a match, events in the order they happened.
      if (aDate !== bDate) return bDate - aDate;
      return a.second - b.second;
    });
  }, [data]);

  const allClips = useMemo(
    () => events.map((e) => ({
      ...e,
      start: Math.max(0, e.videoSecond - secondsBefore),
      end: e.videoSecond + secondsAfter,
    })),
    [events, secondsBefore, secondsAfter],
  );

  const downloadableClips = useMemo(() => allClips.filter((clip) => (
    typeof clip.videoLink === 'string' && clip.videoLink.trim()
    && Number.isFinite(clip.start) && Number.isFinite(clip.end) && clip.end > clip.start
  )), [allClips]);

  useEffect(() => () => {
    playlistDownloadController.current?.abort();
    playlistDownloadController.current = null;
  }, [token]);

  const groups = useMemo(() => groupClips(allClips, grouping), [allClips, grouping]);
  const visibleGroups = useMemo(
    () => selectedGroup === 'all' ? groups : groups.filter((group) => group.key === selectedGroup),
    [groups, selectedGroup],
  );
  const clips = useMemo(() => visibleGroups.flatMap((group) => group.clips), [visibleGroups]);
  const clipIndices = useMemo(() => new Map(clips.map((clip, index) => [clip.key, index])), [clips]);

  const changeGrouping = (mode) => {
    const currentKey = clips[activeIndex]?.key;
    const nextClips = groupClips(allClips, mode).flatMap((group) => group.clips);
    setGrouping(mode);
    setSelectedGroup('all');
    setActiveIndex(Math.max(0, nextClips.findIndex((clip) => clip.key === currentKey)));
  };

  const changeGroup = (key) => {
    const currentKey = clips[activeIndex]?.key;
    const nextClips = (key === 'all' ? groups : groups.filter((group) => group.key === key))
      .flatMap((group) => group.clips);
    setSelectedGroup(key);
    setActiveIndex(Math.max(0, nextClips.findIndex((clip) => clip.key === currentKey)));
  };

  const activeClip = clips[activeIndex] ?? null;
  const activeMatch = activeClip?.match ?? null;
  const clipKey = activeClip
    ? JSON.stringify([token, activeClip.key, activeClip.videoLink, activeClip.start, activeClip.end])
    : null;
  const activeVideoLink = preparedClip?.key === clipKey ? preparedClip.url : null;
  const clipFailed = preparedClip?.key === clipKey && preparedClip.failed;

  useEffect(() => {
    if (!activeClip?.videoLink || !clipKey) return undefined;
    const cached = clipCache.current.get(clipKey);
    if (cached) {
      setPreparedClip({ key: clipKey, url: cached });
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    const prepareClip = async () => {
      try {
        const source = decodeURI(activeClip.videoLink);
        const response = await axios.post(`${VIDEO_SERVICE_URL}/cut`, {
          path: source,
          start: activeClip.start,
          end: activeClip.end,
        }, { signal: controller.signal });
        if (cancelled) return;
        const result = classifyCutResult(response.data);
        const url = response.data?.data?.path;
        if (!result.playable || typeof url !== 'string' || decodeURI(url) === source) {
          throw new Error('Clip unavailable');
        }
        clipCache.current.set(clipKey, url);
        setPreparedClip({ key: clipKey, url });
      } catch {
        if (!cancelled) setPreparedClip({ key: clipKey, url: null, failed: true });
      }
    };
    prepareClip();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [activeClip, clipKey]);

  const playClip = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    const p = video.play();
    if (p?.catch) p.catch(() => {});
  };

  const advanceClip = () => {
    setActiveIndex((index) => Math.min(index + 1, clips.length - 1));
  };

  useEffect(() => {
    if (!clips.length) return undefined;
    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target instanceof Element && event.target.closest(
        'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="slider"], [role="textbox"], [data-playlist-download-menu]',
      )) return;
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      if (event.shiftKey) {
        event.preventDefault();
        setActiveIndex((index) => Math.max(0, Math.min(index + direction, clips.length - 1)));
        return;
      }
      const video = videoRef.current;
      if (!video || !Number.isFinite(video.duration)) return;
      event.preventDefault();
      video.currentTime = Math.max(0, Math.min(video.currentTime + direction * 5, video.duration));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [clips.length]);

  const downloadClip = async () => {
    if (!activeVideoLink || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    let objectUrl;
    let link;
    try {
      const response = await axios.get(activeVideoLink, { responseType: 'blob' });
      objectUrl = URL.createObjectURL(response.data);
      link = document.createElement('a');
      link.href = objectUrl;
      const title = (data?.title || 'SmrtStats').replace(/[^a-z0-9_-]+/gi, '-');
      link.download = `${title}-clip-${activeIndex + 1}.mp4`;
      document.body.appendChild(link);
      link.click();
    } catch {
      setDownloadError('Could not download this clip. Please try again.');
    } finally {
      link?.remove();
      // Give the browser time to start reading the blob before releasing it.
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!formatMenuOpen) return undefined;
    playlistDownloadMenu.current?.querySelector('[data-download-format]')?.focus();
    const onPointerDown = (event) => {
      if (!playlistDownloadMenu.current?.contains(event.target)
        && !playlistDownloadTrigger.current?.contains(event.target)) setFormatMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setFormatMenuOpen(false);
        playlistDownloadTrigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [formatMenuOpen]);

  const downloadPlaylist = async (format) => {
    if (!downloadableClips.length || playlistDownloadController.current) return;
    setFormatMenuOpen(false);
    setPlaylistFormat(format);
    playlistDownloadTrigger.current?.focus();
    const controller = new AbortController();
    playlistDownloadController.current = controller;
    setDownloadingPlaylist(true);
    setPlaylistDownloadError(null);
    let link;
    try {
      const cuts = downloadableClips.map((clip) => ({
        path: decodeURI(clip.videoLink),
        start: Math.max(0, Math.floor(clip.start)),
        end: Math.ceil(clip.end),
      }));
      const response = await axios.post(`${VIDEO_SERVICE_URL}/batch/cut/${format}`, cuts, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const path = response.data?.data?.path;
      if (!classifyCutResult(response.data).playable || typeof path !== 'string') {
        throw new Error('Playlist download unavailable');
      }
      const url = new URL(path);
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid download URL');
      if (format === 'video') url.searchParams.set('download', 'true');
      const title = (data?.title || 'SmrtStats').replace(/[^a-z0-9_-]+/gi, '-');
      link = document.createElement('a');
      link.href = url.href;
      link.download = `${title}-playlist.${format === 'video' ? 'mp4' : 'zip'}`;
      document.body.appendChild(link);
      link.click();
    } catch {
      if (!controller.signal.aborted) {
        setPlaylistDownloadError('Could not download this playlist. Please try again.');
      }
    } finally {
      link?.remove();
      if (playlistDownloadController.current === controller) {
        playlistDownloadController.current = null;
        setDownloadingPlaylist(false);
      }
    }
  };

  // Pause when the tab goes background — see the matching note in
  // /pages/player/EventsVideo.jsx; iOS Safari's MediaController throws on
  // restore otherwise.
  useEffect(() => {
    const onVisibility = () => {
      const video = videoRef.current;
      if (!video) return;
      if (document.hidden) {
        try { video.pause(); } catch { /* ignore */ }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return (
    <ThemeProvider theme={darkTheme}>
      <Shell>
        <TopBar>
          <Brand to="/">
            <BrandLogo src={logo} alt="SmrtStats" />
          </Brand>
          <LoginLink to={`/login?next=${encodeURIComponent(`/shared/playlist/${token}${search}`)}`}>
            Log in
          </LoginLink>
        </TopBar>

        <Main>
          {loading && <Muted>Loading…</Muted>}
          {error && <ErrorBox>Could not load this playlist.</ErrorBox>}
          {expired && (
            <ExpiredCard>
              <ExpiredTitle>This share link has expired</ExpiredTitle>
              <ExpiredHint>
                Share links stay live for a limited time. Ask the player who
                shared it to send you a fresh link from the SmrtStats app.
              </ExpiredHint>
            </ExpiredCard>
          )}

          {!loading && !error && !expired && data && (
            <>
              {data.title && <Title>{data.title}</Title>}

              {activeMatch && (
                <MatchStrip>
                  {activeMatch.competition && (
                    <CompetitionLine>{activeMatch.competition}</CompetitionLine>
                  )}
                  <Teams>
                    <TeamName>{activeMatch.home_team}</TeamName>
                    {activeMatch.score && <Score>{activeMatch.score}</Score>}
                    <TeamName>{activeMatch.away_team}</TeamName>
                  </Teams>
                  {activeClip.date && (
                    <DateLine>
                      <Moment format="DD.MM.YYYY">{activeClip.date}</Moment>
                    </DateLine>
                  )}
                </MatchStrip>
              )}

              {activeVideoLink ? (
                <VideoStage>
                  <StyledVideo
                    key={clipKey}
                    aria-label="Event video clip"
                    ref={videoRef}
                    src={activeVideoLink}
                    onLoadedMetadata={playClip}
                    onEnded={advanceClip}
                    onError={() => setPreparedClip({ key: clipKey, url: null, failed: true })}
                    controls
                    playsInline
                    preload="metadata"
                  />
                </VideoStage>
              ) : activeClip?.videoLink && !clipFailed ? (
                <UnavailableStage role="status">
                  <UnavailableLabel>Preparing clip…</UnavailableLabel>
                </UnavailableStage>
              ) : events.length > 0 ? (
                <UnavailableStage>
                  <UnavailableLabel>Video unavailable</UnavailableLabel>
                </UnavailableStage>
              ) : (
                <Muted>This playlist has no playable events.</Muted>
              )}

              {activeClip && (
                <DownloadRow>
                  <NavButton type="button" disabled={!activeVideoLink || downloading} onClick={downloadClip}>
                    {downloading ? 'Downloading…' : 'Download clip'}
                  </NavButton>
                  <PlaylistDownloadOptions>
                    <NavButton
                      ref={playlistDownloadTrigger}
                      type="button"
                      disabled={!downloadableClips.length || downloadingPlaylist}
                      aria-busy={downloadingPlaylist}
                      aria-haspopup="dialog"
                      aria-expanded={formatMenuOpen}
                      aria-controls="playlist-download-format"
                      onClick={() => setFormatMenuOpen((open) => !open)}
                    >
                      {downloadingPlaylist
                        ? (playlistFormat === 'video' ? 'Preparing video…' : 'Preparing ZIP…')
                        : 'Download Playlist'}
                    </NavButton>
                    {formatMenuOpen && (
                      <DownloadFormatMenu
                        ref={playlistDownloadMenu}
                        id="playlist-download-format"
                        role="dialog"
                        aria-label="Download playlist format"
                        data-playlist-download-menu
                        onBlur={(event) => {
                          if (!event.currentTarget.contains(event.relatedTarget)
                            && event.relatedTarget !== playlistDownloadTrigger.current) setFormatMenuOpen(false);
                        }}
                      >
                        <DownloadFormatHeading>
                          <span>Choose format</span>
                          <CloseFormatMenu
                            type="button"
                            aria-label="Close format choices"
                            onClick={() => {
                              setFormatMenuOpen(false);
                              playlistDownloadTrigger.current?.focus();
                            }}
                          >×</CloseFormatMenu>
                        </DownloadFormatHeading>
                        <DownloadFormatOption type="button" data-download-format onClick={() => downloadPlaylist('zip')}>
                          ZIP · separate clips
                        </DownloadFormatOption>
                        <DownloadFormatOption type="button" data-download-format onClick={() => downloadPlaylist('video')}>
                          MP4 · one video
                        </DownloadFormatOption>
                      </DownloadFormatMenu>
                    )}
                  </PlaylistDownloadOptions>
                  {downloadableClips.length > 0 && downloadableClips.length < allClips.length && (
                    <DownloadNote>
                      {allClips.length - downloadableClips.length} {allClips.length - downloadableClips.length === 1 ? 'event has' : 'events have'} no video and will be skipped.
                    </DownloadNote>
                  )}
                  {playlistDownloadError && <DownloadError role="alert">{playlistDownloadError}</DownloadError>}
                  {downloadError && <DownloadError role="alert">{downloadError}</DownloadError>}
                </DownloadRow>
              )}

              {activeClip && (
                <NowPlayingStrip>
                  <NowPlayingName>{activeClip.paramName}</NowPlayingName>
                  <NowPlayingTime>{formatSeconds(activeClip.second)}</NowPlayingTime>
                </NowPlayingStrip>
              )}

              {clips.length > 0 && (
                <NavBar>
                  <NavButton
                    type="button"
                    disabled={activeIndex <= 0}
                    onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
                  >
                    ‹ Prev
                  </NavButton>
                  <Counter>{activeIndex + 1} / {clips.length}</Counter>
                  <NavButton
                    type="button"
                    disabled={activeIndex >= clips.length - 1}
                    onClick={() => setActiveIndex((i) => Math.min(clips.length - 1, i + 1))}
                  >
                    Next ›
                  </NavButton>
                </NavBar>
              )}

              {clips.length > 0 && (
                <KeyboardHint>← / → Seek 5s · Shift + ← / → Previous / next clip</KeyboardHint>
              )}

              {events.length > 0 && (
                <>
                  <GroupingControls>
                    {grouping !== 'none' && (
                      <SelectLabel>
                        {grouping === 'match' ? 'Choose match' : 'Choose event type'}
                        <GroupSelect value={selectedGroup} onChange={(event) => changeGroup(event.target.value)}>
                          <option value="all">{grouping === 'match' ? 'All matches' : 'All event types'}</option>
                          {groups.map((group) => (
                            <option key={group.key} value={group.key}>
                              {group.date ? `${group.date.slice(0, 10)} · ` : ''}{group.label} ({group.clips.length})
                            </option>
                          ))}
                        </GroupSelect>
                      </SelectLabel>
                    )}
                    <SelectLabel $groupBy>
                      Group by
                      <GroupSelect value={grouping} onChange={(event) => changeGrouping(event.target.value)}>
                        <option value="none">No grouping</option>
                        <option value="match">Match</option>
                        <option value="type">Event type</option>
                      </GroupSelect>
                    </SelectLabel>
                  </GroupingControls>
                  <ClipsList>
                    {visibleGroups.map((group) => (
                      <ClipGroup key={group.key} aria-label={grouping !== 'none' ? group.label : undefined}>
                        {grouping !== 'none' && (
                          <GroupHeading>
                            <GroupTitle>{group.label}</GroupTitle>
                            <GroupMeta>
                              {group.date && <><Moment format="DD.MM.YYYY">{group.date}</Moment> · </>}
                              {group.clips.length} {group.clips.length === 1 ? 'event' : 'events'}
                            </GroupMeta>
                          </GroupHeading>
                        )}
                        {group.clips.map((c) => {
                          const idx = clipIndices.get(c.key);
                          return (
                            <ClipRow
                              key={c.key}
                              $active={idx === activeIndex}
                              aria-current={idx === activeIndex ? 'true' : undefined}
                              type="button"
                              onClick={() => setActiveIndex(idx)}
                            >
                              <ClipIndex>{idx + 1}</ClipIndex>
                              <ClipBody>
                                <ClipName>{c.paramName}</ClipName>
                                <ClipMeta>
                                  {c.date && <><Moment format="DD.MM.YYYY">{c.date}</Moment> · </>}
                                  {c.match?.home_team} vs {c.match?.away_team} · {formatSeconds(c.second)}
                                </ClipMeta>
                              </ClipBody>
                            </ClipRow>
                          );
                        })}
                      </ClipGroup>
                    ))}
                  </ClipsList>
                </>
              )}

              <Footer>
                Shared from SmrtStats Player ·{' '}
                <FooterLink to="/">Learn more</FooterLink>
              </Footer>
            </>
          )}
        </Main>
      </Shell>
    </ThemeProvider>
  );
};

export default SharedPlaylist;

const Shell = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
  min-height: 100vh;
  min-height: 100dvh;
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px max(16px, env(safe-area-inset-right)) 12px max(16px, env(safe-area-inset-left));
  border-bottom: 1px solid ${(props) => props.theme.borderColor};
`;

const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
`;

const BrandLogo = styled.img`
  height: 24px;
`;

const Main = styled.main`
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));

  @media (max-width: 480px) {
    padding-left: max(12px, env(safe-area-inset-left));
    padding-right: max(12px, env(safe-area-inset-right));
  }
`;

const Title = styled.h1`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 20px;
  line-height: 1.3;
  margin: 0;
  color: ${(props) => props.theme.white};
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
`;

const ExpiredCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 24px;
  text-align: center;
  border: 1px dashed ${(props) => props.theme.borderColor};
  border-radius: 16px;
`;

const ExpiredTitle = styled.div`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 16px;
  color: ${(props) => props.theme.white};
`;

const ExpiredHint = styled.p`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  color: ${(props) => props.theme.blueish2};
  margin: 0;
  line-height: 1.4;
  max-width: 320px;
`;

const MatchStrip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px;
  background: ${(props) => props.theme.row};
  border-radius: 12px;
`;

const CompetitionLine = styled.div`
  font-size: 11px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(props) => props.theme.blueish2};
`;

const Teams = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 12px;
  text-align: center;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 15px;
`;

const TeamName = styled.span`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Score = styled.span`
  flex-shrink: 0;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 17px;
  color: ${(props) => props.theme.green};
`;

const DateLine = styled.div`
  font-size: 11px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${(props) => props.theme.blueish2};
`;

const VideoStage = styled.div`
  width: 100%;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 16 / 9;
`;

const StyledVideo = styled.video`
  object-fit: contain;
  width: 100%;
  height: 100%;
  display: block;
`;

const UnavailableStage = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${(props) => props.theme.row};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UnavailableLabel = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const NowPlayingStrip = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 0 4px;
`;

const NowPlayingName = styled.span`
  min-width: 0;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
`;

const NowPlayingTime = styled.span`
  flex-shrink: 0;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 12px;
  color: ${(props) => props.theme.green};
  letter-spacing: 0.4px;
`;

const NavBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 4px 0;
`;

const NavButton = styled.button`
  min-height: 44px;
  touch-action: manipulation;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background: transparent;
  color: ${(props) => props.theme.white};
  cursor: pointer;
  font-size: 12px;

  &:disabled { opacity: 0.4; cursor: default; }
  &:focus-visible { outline: 2px solid ${(props) => props.theme.green}; outline-offset: 2px; }
  @media (max-width: 480px) { flex: 1; }
`;

const Counter = styled.span`
  flex-shrink: 0;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 12px;
  letter-spacing: 0.4px;
`;

const ClipsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const ClipRow = styled.button`
  width: 100%;
  box-sizing: border-box;
  min-height: 56px;
  touch-action: manipulation;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid ${(props) => (props.$active ? props.theme.green : props.theme.borderColor)};
  background: ${(props) => (props.$active ? 'rgba(216, 255, 67, 0.05)' : 'transparent')};
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  color: inherit;
`;

const ClipIndex = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  min-width: 24px;
  flex-shrink: 0;
`;

const ClipBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const ClipName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
`;

const ClipMeta = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  white-space: normal;
  overflow-wrap: anywhere;
`;

const Footer = styled.footer`
  margin-top: 24px;
  padding: 16px 0;
  text-align: center;
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
`;

const FooterLink = styled(Link)`
  color: ${(props) => props.theme.green};
  text-decoration: none;
`;

const LoginLink = styled(Link)`
  color: ${(props) => props.theme.white};
  font-size: 14px;
`;

const DownloadRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const DownloadError = styled.span`
  font-size: 13px;
  color: ${(props) => props.theme.white};
`;

const KeyboardHint = styled.p`
  margin: 0;
  color: ${(props) => props.theme.blueish2};
  font-size: 12px;
  text-align: center;
  @media (hover: none) and (pointer: coarse) { display: none; }
`;

const GroupingControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  @media (min-width: 641px) {
    align-items: center;
    gap: 10px;
  }
`;

const SelectLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 0 1 ${(props) => props.$groupBy ? '160px' : '200px'};
  margin-left: ${(props) => props.$groupBy ? 'auto' : '0'};
  min-width: 0;
  font-size: 13px;
  color: ${(props) => props.theme.white};
  @media (min-width: 641px) {
    flex: 0 1 auto;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }
`;

const GroupSelect = styled.select`
  width: 100%;
  min-width: 0;
  min-height: 44px;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background: ${(props) => props.theme.row};
  color: ${(props) => props.theme.white};
  font-size: 16px;
  &:focus-visible { outline: 2px solid ${(props) => props.theme.green}; outline-offset: 2px; }
  @media (min-width: 641px) {
    width: 160px;
    min-height: 34px;
    padding: 6px 10px;
    font-size: 14px;
  }
`;

const ClipGroup = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 6px;
  & + & { margin-top: 12px; }
`;

const GroupHeading = styled.div`
  padding: 8px 0;
`;

const GroupTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  line-height: 1.4;
`;

const GroupMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
`;

const DownloadNote = styled.span`
  flex-basis: 100%;
  color: ${(props) => props.theme.blueish2};
  font-size: 12px;
`;

const PlaylistDownloadOptions = styled.div`
  position: relative;
  margin-left: auto;
  min-width: 0;
`;

const DownloadFormatMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 10;
  width: 240px;
  max-width: calc(100vw - 24px);
  box-sizing: border-box;
  padding: 8px;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 12px;
  color: ${(props) => props.theme.white};
  background: ${(props) => props.theme.row};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`;

const DownloadFormatHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 8px;
  font-size: 13px;
`;

const DownloadFormatOption = styled.button`
  display: block;
  width: 100%;
  min-height: 44px;
  padding: 10px;
  border: 0;
  border-radius: 8px;
  text-align: left;
  color: ${(props) => props.theme.white};
  background: transparent;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.08); }
  &:focus-visible { outline: 2px solid ${(props) => props.theme.green}; }
`;

const CloseFormatMenu = styled.button`
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 8px;
  color: ${(props) => props.theme.white};
  background: transparent;
  font-size: 22px;
  cursor: pointer;
  &:focus-visible { outline: 2px solid ${(props) => props.theme.green}; }
`;
